import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { registerUser } from "../../redux/authSlice";
import { useNavigate } from "react-router-dom";
import { Checkbox, FormControlLabel, FormGroup } from "@mui/material";

import { toast } from "react-toastify";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import IconButton from "@mui/material/IconButton";
import PolicyModal from "../../common/Policy/PolicyModal.jsx";

const SignUpForm = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { loading, error } = useSelector((state) => state.auth);

  const [showPassword, setShowPassword] = useState(false);
  const [showPasswordConfirm, setShowPasswordConfirm] = useState(false);
  const [isPolicyModalOpen, setPolicyModalOpen] = useState(false);

  const userAgreementContent = t("userAgreementContent", {
    returnObjects: true,
  });
  const explicitConsentContent = t("explicitConsentFormContent", {
    returnObjects: true,
  });
  const commercialConsentContent = t(
    "commercialElectronicMessageConsentFormContent",
    { returnObjects: true },
  );

  const renderPolicyBlocks = (blocks) => {
    if (!Array.isArray(blocks)) {
      return <p className="text-gray-700">{blocks}</p>;
    }

    return blocks.map((block, index) => {
      if (block.type === "heading") {
        const HeadingTag =
          block.level === 4 ? "h4" : block.level === 3 ? "h3" : "h2";
        const headingBase = "font-semibold text-primary";
        const headingSizing =
          block.level === 2
            ? "text-2xl"
            : block.level === 3
              ? "text-xl"
              : "text-lg";
        const isPrimaryHeading = index === 0;
        const headingClasses = [
          isPrimaryHeading ? "mt-0" : "mt-6",
          headingBase,
          isPrimaryHeading ? "text-3xl text-center" : headingSizing,
        ].join(" ");
        return (
          <HeadingTag key={`heading-${index}`} className={headingClasses}>
            {block.content}
          </HeadingTag>
        );
      }

      if (block.type === "paragraph") {
        return (
          <p key={`paragraph-${index}`} className="mt-2 text-gray-700">
            {block.content}
          </p>
        );
      }

      if (block.type === "list") {
        return (
          <ul
            key={`list-${index}`}
            className="mt-2 list-disc space-y-1 pl-6 text-gray-700 marker:text-primary"
          >
            {block.items?.map((item, itemIndex) => (
              <li key={`list-${index}-${itemIndex}`}>{item}</li>
            ))}
          </ul>
        );
      }

      return null;
    });
  };

  const [formData, setFormData] = useState({
    name: "",
    surname: "",
    email: "",
    phoneNumber: "",
    countryCode: "+90",
    password: "",
    passwordConfirm: "",
    policy1: false,
    policy2: false,
    policy3: false,
  });

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.policy1 || !formData.policy2 || !formData.policy3) {
      toast.error("Please accept all agreements.", {
        autoClose: 8000,
      });
      return;
    }

    if (formData.password !== formData.passwordConfirm) {
      toast.error("Passwords do not match!", {
        autoClose: 8000,
      });
      return;
    }

    const requestBody = {
      name: formData.name,
      surname: formData.surname,
      email: formData.email,
      password: formData.password,
      contact: {
        countryCode: formData.countryCode,
        phoneNumber: formData.phoneNumber,
        isVerified: false,
      },
      memberAg: formData.policy1,
      infoText: formData.policy2,
      perData: formData.policy3,
    };

    try {
      // Perform registration
      await dispatch(registerUser(requestBody)).unwrap();

      // Show email verification message and inform user
      toast.success(
        "Registration successful! A verification link has been sent to your email address.",
        {
          autoClose: 10000,
        },
      );

      // Onboarding survey control is handled on first sign-in.
      navigate("/signIn");
    } catch (err) {
      console.error("Registration error:", err);
      console.error("Error details:", err.response || err.message || err);
      toast.error(
        err.response?.data?.message || "An error occurred during registration.",
        {
          autoClose: 9000,
        },
      );
    }
  };

  return (
    <div className="mt-[22px]">
      <p className="text-text font-poppins text-[20px] ml-4 mb-4">
        {t("signInTitle")}
      </p>

      <div className="overflow-hidden">
        <div>
          <input
            type="text"
            placeholder={t("name")}
            className="w-full px-4 py-2 border-2 border-cards-border focus:ring-2 focus:ring-[#1B5E20] rounded-tr-[30px] rounded-tl-[30px]"
            value={formData.name}
            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
          />

          <input
            type="text"
            placeholder={t("surname")}
            className="w-full px-4 py-2 border-2 border-cards-border focus:ring-2 focus:ring-[#1B5E20]"
            value={formData.surname}
            onChange={(e) =>
              setFormData({ ...formData, surname: e.target.value })
            }
          />

          <input
            type="email"
            placeholder="E-mail"
            className="w-full px-4 py-2 border-2 border-cards-border focus:ring-2 focus:ring-[#1B5E20]"
            value={formData.email}
            required
            pattern="[a-z0-9._%+\-]+@[a-z0-9.\-]+\.[a-z]{2,}$"
            onChange={(e) =>
              setFormData({ ...formData, email: e.target.value })
            }
          />

          <div className="flex items-center border-2 border-cards-border">
            <select
              className="px-4 py-2 text-[16px] text-text font-poppins focus:ring-2 focus:ring-[#1B5E20] border-r-2 border-cards-border"
              value={formData.countryCode}
              onChange={(e) =>
                setFormData({ ...formData, countryCode: e.target.value })
              }
            >
              <option value="+90">+90</option>
              <option value="+1">+1</option>
              <option value="+44">+44</option>
              <option value="+49">+49</option>
            </select>

            <input
              type="tel"
              placeholder={t("phone")}
              pattern="[0-9]*"
              inputMode="numeric"
              className="w-full px-4 py-2 bg-white text-[16px] focus:outline-none focus:ring-2 focus:ring-[#1B5E20]"
              value={formData.phoneNumber}
              maxLength={10}
              required
              onChange={(e) =>
                setFormData({
                  ...formData,
                  phoneNumber: e.target.value.replace(/\D/g, ""),
                })
              }
            />
          </div>

          <div className="relative">
            <input
              type={showPassword ? "text" : "password"}
              placeholder={t("password")}
              className="w-full px-4 py-2 border-2 border-cards-border focus:outline-none focus:ring-2 focus:ring-[#1B5E20]"
              value={formData.password}
              required
              onChange={(e) =>
                setFormData({ ...formData, password: e.target.value })
              }
            />
            <IconButton
              onClick={() => setShowPassword((prev) => !prev)}
              edge="end"
              sx={{
                position: "absolute",
                right: "36px",
                top: "50%",
                transform: "translateY(-50%)",
              }}
            >
              {showPassword ? <VisibilityOff /> : <Visibility />}
            </IconButton>
          </div>

          <div className="relative">
            <input
              type={showPasswordConfirm ? "text" : "password"}
              placeholder={t("passwordConfirm")}
              className="w-full px-4 py-2 border-2 border-cards-border rounded-bl-[30px] rounded-br-[30px] focus:outline-none focus:ring-2 focus:ring-[#1B5E20]"
              value={formData.passwordConfirm}
              required
              onChange={(e) =>
                setFormData({ ...formData, passwordConfirm: e.target.value })
              }
            />
            <IconButton
              onClick={() => setShowPasswordConfirm((prev) => !prev)}
              edge="end"
              sx={{
                position: "absolute",
                right: "36px",
                top: "50%",
                transform: "translateY(-50%)",
              }}
            >
              {showPasswordConfirm ? <VisibilityOff /> : <Visibility />}
            </IconButton>
          </div>

          <p className="text-[#454545] font-poppins text-[20px] ml-4 mt-7">
            {t("formInform")}
          </p>

          <FormGroup
            sx={{
              p: 3,
              gap: 3,
            }}
            className="rounded-2xl border border-[#625480]/15 bg-white/60 shadow-sm"
          >
            <FormControlLabel
              control={
                <Checkbox
                  required
                  checked={formData.policy1}
                  onChange={(e) =>
                    setFormData({ ...formData, policy1: e.target.checked })
                  }
                  name="policy1"
                  sx={{
                    "&.MuiCheckbox-root": {
                      borderRadius: "6px",
                      width: "24px",
                      height: "24px",
                    },
                    color: "#625480",
                    "&.Mui-checked": {
                      color: "#625480",
                    },
                  }}
                />
              }
              label={
                <span className="flex flex-col gap-2 text-left">
                  <span className="text-[18px] font-medium leading-relaxed text-[#1F1F1F]">
                    {t("policy-1")}
                  </span>
                  <button
                    type="button"
                    onClick={() => setPolicyModalOpen("policy1")}
                    className="self-start rounded-full border border-[#625480]/40 px-3 py-1 text-sm font-medium text-[#625480] transition hover:bg-[#625480] hover:text-white"
                  >
                    {t("viewDocument")}
                  </button>
                </span>
              }
              sx={{
                width: "100%",
                alignItems: "flex-start",
                "& .MuiFormControlLabel-label": {
                  marginLeft: "12px",
                },
                mb: 2,
              }}
            />

            <FormControlLabel
              control={
                <Checkbox
                  required
                  checked={formData.policy2}
                  onChange={(e) =>
                    setFormData({ ...formData, policy2: e.target.checked })
                  }
                  name="policy2"
                  sx={{
                    "&.MuiCheckbox-root": {
                      borderRadius: "6px",
                      width: "24px",
                      height: "24px",
                    },
                    color: "#625480",
                    "&.Mui-checked": {
                      color: "#625480",
                    },
                  }}
                />
              }
              label={
                <span className="flex flex-col gap-2 text-left">
                  <span className="text-[18px] font-medium leading-relaxed text-[#1F1F1F]">
                    {t("policy-2")}
                  </span>
                  <button
                    type="button"
                    onClick={() => setPolicyModalOpen("policy2")}
                    className="self-start rounded-full border border-[#625480]/40 px-3 py-1 text-sm font-medium text-[#625480] transition hover:bg-[#625480] hover:text-white"
                  >
                    {t("viewDocument")}
                  </button>
                </span>
              }
              sx={{
                width: "100%",
                alignItems: "flex-start",
                "& .MuiFormControlLabel-label": {
                  marginLeft: "12px",
                },
                mb: 2,
              }}
            />

            <FormControlLabel
              control={
                <Checkbox
                  required
                  checked={formData.policy3}
                  onChange={(e) =>
                    setFormData({ ...formData, policy3: e.target.checked })
                  }
                  name="policy3"
                  sx={{
                    "&.MuiCheckbox-root": {
                      borderRadius: "6px",
                      width: "24px",
                      height: "24px",
                    },
                    color: "#625480",
                    "&.Mui-checked": {
                      color: "#625480",
                    },
                  }}
                />
              }
              label={
                <span className="flex flex-col gap-2 text-left">
                  <span className="text-[18px] font-medium leading-relaxed text-[#1F1F1F]">
                    {t("policy-3")}
                  </span>
                  <button
                    type="button"
                    onClick={() => setPolicyModalOpen("policy3")}
                    className="self-start rounded-full border border-[#625480]/40 px-3 py-1 text-sm font-medium text-[#625480] transition hover:bg-[#625480] hover:text-white"
                  >
                    {t("viewDocument")}
                  </button>
                </span>
              }
              sx={{
                width: "100%",
                alignItems: "flex-start",
                "& .MuiFormControlLabel-label": {
                  marginLeft: "12px",
                },
                mb: 0,
              }}
            />
          </FormGroup>
          {error && <p className="text-red-500 ml-4">{error}</p>}

          <button
            onClick={handleSubmit}
            className={`w-full h-[61px] ${
              loading ? "bg-gray-400" : "bg-button-bg"
            } text-white py-2 rounded-full text-[20px] font-poppins mt-6`}
            disabled={loading}
          >
            {loading ? "Registering..." : t("signUp")}
          </button>
        </div>
      </div>
      <PolicyModal
        isOpen={Boolean(isPolicyModalOpen)}
        onClose={() => setPolicyModalOpen(false)}
        title={
          isPolicyModalOpen === "policy1"
            ? t("userAgreement")
            : isPolicyModalOpen === "policy2"
              ? t("explicitConsentForm")
              : isPolicyModalOpen === "policy3"
                ? t("commercialElectronicMessageConsentForm")
                : ""
        }
      >
        {isPolicyModalOpen === "policy1" && (
          <div className="space-y-4 text-sm leading-6 text-gray-700">
            {renderPolicyBlocks(userAgreementContent)}
          </div>
        )}
        {isPolicyModalOpen === "policy2" && (
          <div className="space-y-4 text-sm leading-6 text-gray-700">
            {renderPolicyBlocks(explicitConsentContent)}
          </div>
        )}
        {isPolicyModalOpen === "policy3" && (
          <div className="space-y-4 text-sm leading-6 text-gray-700">
            {renderPolicyBlocks(commercialConsentContent)}
          </div>
        )}
      </PolicyModal>
    </div>
  );
};

export default SignUpForm;
